import React from 'react';
import { getCurrencyIcon } from '../../../../../../../shared/js/get-currency-icon';
import { formatMoney } from '../../../../shared/format';
import find from 'lodash/find';
import isNull from 'lodash/isNull';
import keys from 'lodash/keys';
import { enum as notificationEventTypes } from '../../../../../../../../enums/notification-event-types';
import avatars from '../../../../../config/avatars';

const threadActions = ['new_thread', 'closed_thread', 'paused_thread', 'unpaused_thread'];

const isRocket = process.env.CPA_ENV === 'rocket';

const formatForComparing = (newObj, old) =>
	keys(newObj).reduce((acc, item) => ({ ...acc,
		[item]: {
			'old': old[item],
			'new': newObj[item]
		}
	}), {});

export const getTimelineItemAvatar = item => {
	if (item.thread && threadActions.indexOf(item.action) !== -1) {
		const isClosedAuto = !isNull(item.thread.close_message) && isNull(item.thread.closed_admin_id);
		if (item.thread_action_user) {
			return (
				item.thread_action_user.avatar ||
				(item.thread_action_user.id != item.affiliate_id
					? avatars.adminSmall
					: avatars.affiliateSmall)
			);
		} else if (item.thread.admin_id && item.admin_avatar) {
			return item.admin_avatar;
		}
		return isClosedAuto
			? avatars.adminSmall
			: avatars.affiliateSmall;
	}
	if (item.metadata && item.metadata.type === 'change_terms' && item.action === 'affiliate_ind_terms_change') {
		if (!isNull(item.master_account)) {
			return item.master_account.avatar || avatars.affiliateSmall;
		} else if (isNull(item.admin_id)) {
			return avatars.adminSmall;
		}
	}
	if (
		item.metadata &&
		item.metadata.type === 'general_fields' &&
		item.action === 'affiliate_fields_change' &&
		item.metadata.is_system_change
	) {
		return avatars.adminSmall;
	}

	if (item.action === 'payout_status_change' && item.metadata && item.metadata.is_system_change) {
		return avatars.adminSmall; // like system icon
	}

	return item.admin_id
		? item.admin_avatar || avatars.adminSmall
		: item.affiliate_avatar || avatars.affiliateSmall;
};

export const getPayoutItemText = (item, intl) => {
	const { amount_with_percent, amount, is_system_change, credentials, status: payoutStatus, isFraud } = item.metadata;

	const credential = isRocket && payoutStatus === 'paid' && !!credentials ? getMainCredential(credentials) : '';

	const entity = (
		<span>
			<span className="black">
				{' '}
				{formatMoney(amount, true)} {getCurrencyIcon(item.payout.currency)}
				{}
				{amount_with_percent && payoutStatus === 'paid' &&
					' (' + formatMoney(amount_with_percent, true) +
					' ' + getCurrencyIcon(item.payout.currency) + ')'}
			</span>
			<span>{` (${item.payout.payout_system_title}${credential && `: ${credential}`})`}</span>
			<span>{` ${isFraud === 'true' ? 'because of fraud' : ''}`}</span>
		</span>
	);

	if (is_system_change) {
		return {
			email: intl('system'),
			action: intl('%s payout for', payoutStatus),
			entity
		};
	}

	switch (payoutStatus) {
		case 'pending':
			return {
				entity,
				email: item.admin_email || item.affiliate_email,
				action: intl('requested payout for')
			};
		case 'approved':
			return {
				email: item.admin_email || item.affiliate_email,
				action: intl(item.admin_email ? 'approved payout for' : 'requested (auto approve) payout for'),
				entity
			};
		case 'rejected':
		case 'confirmed':
		case 'paid':
			return {
				email: item.admin_email,
				action: intl('%s payout for', payoutStatus),
				entity
			};
		default:
			return {};
	}
};

export const getNonTerminalItemText = (item, intl) => ({
	email: intl('Non terminal status was changed.'),
	entity: (
		<span>Offer <span className='black'>#{item.offer_id}</span>
			<span>, Conversion <span className='black'>#{item.metadata.conversion_id}</span></span>
		</span>
	),
	fields: formatForComparing(item.metadata.new, item.metadata.old)
});

export const getConversionPayoutReviewItemText = (item, intl) => ({
	email: intl('Scaling payout request conversions.'),
	entity: (
		<span>
			Offer <span className='black'>#{item.offer_id}</span>
			{` Payout request id`} <span>#{item.metadata.payout_request_id}</span>
		</span>
	),
	fields: formatForComparing(item.metadata.new, item.metadata.old)
});

export const getEmailEventText = (item, newsTypesList, intl) => {
	let result = {
		action: intl('opened email')
	};
	let eventType = item.metadata.event_type;
	let isNews = !!item.metadata.news_id;
	if (isNews) {
		let newsType = find(newsTypesList, { id: eventType });
		result = {
			action: intl(`%s %s news email:`, getEmailActionText(item, intl), newsType && newsType.text ? newsType.text : ''),
			entity: item.metadata.news_id &&
				item.news.title && (
				<a href={`news/edit/${item.metadata.news_id}`} target="_blank" className="black">
					{' '}
					{item.news.title}
				</a>
			)
		};
	}
	switch (eventType) {
		case notificationEventTypes.new_thread:
		case notificationEventTypes.new_thread_post:
			result = {
				action:
					eventType === 'new_thread_post'
						? intl(`%s new thread post email:`, getEmailActionText(item, intl))
						: intl(`%s new thread email:`, getEmailActionText(item, intl)),
				entity: item.metadata.thread_id &&
					item.thread.title && (
					<a href={`threads/${item.metadata.thread_id}`} target="_blank" className="black">
						{' '}
						{item.thread.title}
					</a>
				)
			};
			break;
		case notificationEventTypes.password_remind:
			result = {
				action: intl(`%s password remind email`, getEmailActionText(item, intl))
			};
			break;
		case notificationEventTypes.new_affiliate:
			result = {
				action: intl(`%s confirmation email`, getEmailActionText(item, intl))
			};
			break;
		case notificationEventTypes.payout_status_change:
			result = {
				action: intl(`%s payout status change email`, getEmailActionText(item, intl)),
				meta: item.metadata,
				emailPayout: item.payout || null
			};
			break;
		case notificationEventTypes.digest:
			result = {
				action: intl(`%s digest email`, getEmailActionText(item, intl)),
				emailDigest: item.mailing || null
			};
			break;
		case notificationEventTypes.affiliate_confirmed_email:
			result = {
				action: intl(`%s greeting email`, getEmailActionText(item, intl))
			};
			break;
		case notificationEventTypes.daily_news:
			result = {
				action: intl(`%s daily news email`, getEmailActionText(item, intl)),
				emailDailyNews: item.daily_news
			};
			break;
		case notificationEventTypes.inactive_three_days:
			result = {
				action: intl(`%s inactive three days email`, getEmailActionText(item, intl))
			};
			break;
		case notificationEventTypes.inactive_seven_days:
			result = {
				action: intl(`%s inactive seven days email`, getEmailActionText(item, intl))
			};
			break;
		default:
			break;
	}
	return result;
};

export const getThreadItemText = (item, intl) => {
	let result = {
		thread: item.thread,
		action: intl('created ticket')
	};
	let threadCategory = item.metadata.category;
	switch (threadCategory) {
		case 'offer_access':
			result = {
				...result,
				email: item.affiliate_email,
				action: intl('requested access to offer'),
				entity: (
					<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black">
						{' '}
						{item.offer}
					</a>
				)
			};
			break;
		case 'new_landing_transit':
			result = {
				...result,
				email: item.affiliate_email,
				action: intl('requested new landing/transit on offer'),
				entity: (
					<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black">
						{' '}
						{item.offer}
					</a>
				)
			};
			break;
		case 'admin_ticket':
			result.email = item.admin_email;
			break;
		default:
			result.email = item.affiliate_email;
			break;
	}
	return result;
};

const getEmailActionText = (item, intl) => {
	switch (item.action) {
		case 'clicked_email':
			return intl('clicked %s link in', item.metadata.url);
		case 'unsubscribed_email':
		case 'complained_email':
			return intl('unsubscribed from');
		case 'dropped_email':
		case 'bounced_email':
		case 'failed_email':
			return intl('did not receive');
		default:
			return intl('opened');
	}
};

const getMainCredential = (credentials) => {
	const order = { card: 0, account: 1, phone: 2 };

	const key = keys(credentials).filter((f) => f !== 'name')
		.sort((a, b) => (order[a] || Infinity) - (order[b] || Infinity))[0];

	return credentials[key] || credentials.name || '?';
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/profile/timeline/entities.js